import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import apiService from '../services/api';

function clearAdminSessionLocally() {
  try {
    apiService.clearTokens();
  } catch {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');
  }
}

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';
  const isInvite = searchParams.get('invite') === '1';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Ne jamais réutiliser une session Super Admin (ou autre) pendant l'activation.
  useEffect(() => {
    clearAdminSessionLocally();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!tokenFromUrl) {
      setError(
        isInvite
          ? 'Lien d’invitation invalide. Demandez un nouvel envoi.'
          : 'Lien invalide. Refaites une demande de réinitialisation.',
      );
      return;
    }

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      const result = await apiService.adminResetPassword(tokenFromUrl, password);
      clearAdminSessionLocally();
      const email = result?.email || '';
      const activated = result?.purpose === 'admin_invite' || isInvite;
      const params = new URLSearchParams();
      if (activated) params.set('activated', '1');
      else params.set('reset', '1');
      if (email) params.set('email', email);
      // Hard reload : force isAuthenticated=false dans App (évite session SA résiduelle).
      window.location.replace(`/login?${params.toString()}`);
    } catch (err) {
      setError(err.message || 'Impossible d’enregistrer le mot de passe.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-sm">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <Lock className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-2xl font-bold text-gray-900">
            {isInvite ? 'Choisir mon mot de passe' : 'Nouveau mot de passe'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {isInvite
              ? 'Activez votre compte professionnel Clickorico'
              : 'Administration Clickorico'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {!tokenFromUrl && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg text-sm">
              Lien incomplet.{' '}
              <Link to="/mot-de-passe-oublie" className="underline font-medium">
                Demander un nouveau lien
              </Link>
            </div>
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              {isInvite ? 'Mot de passe' : 'Nouveau mot de passe'}
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirmer
            </label>
            <input
              id="confirmPassword"
              type="password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <button
            type="submit"
            disabled={loading || !tokenFromUrl}
            className="w-full py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loading
              ? 'Enregistrement...'
              : isInvite
                ? 'Activer mon compte'
                : 'Enregistrer'}
          </button>

          <a href="/login" className="block text-center text-sm text-primary-600 hover:underline">
            Retour à la connexion
          </a>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
